import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { getStepsForRole } from "./utils/getStepsForRole";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import Error from "../bodyComponents/Error";
import FilterRoleSteps from "./stepsComponents/FilterRoleSteps";
import "../../css/RolePage.css";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

export default function RolePage({ isNavOpen, role }) {
  const { t } = useTranslation();
  const { auth } = useAuth();

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const [steps, setSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stepNameFilter, setStepNameFilter] = useState("");
  const debounceTimer = useRef(null);
  const [favoriteStepIds, setFavoriteStepIds] = useState([]);
  const [updatingFavorites, setUpdatingFavorites] = useState({});

  // ✅ Stable handler so React.memo sees no prop change
  const handleFilterChange = useCallback((value) => {
    setStepNameFilter(value);
  }, []);

  // ✅ Debounced backend fetch
  useEffect(() => {
    if (!role?.role_id || !auth?.userId) return;
    clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(async () => {
      try {
        setIsLoading(true);
        const data = await getStepsForRole(role.role_id, auth.userId, {
          step_name: stepNameFilter,
        });
        setSteps(data);
      } catch (err) {
        console.error("❌ Error fetching steps:", err);
        setError(err.message || "Failed to load steps.");
      } finally {
        setIsLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceTimer.current);
  }, [stepNameFilter, role?.role_id, auth?.userId]);

  // ✅ Fetch favorite steps
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!auth?.userId) return;
      try {
        const res = await axiosPrivate.get(`/steps/favorites/${auth.userId}`);
        const ids = res.data.map((s) => s.step_id);
        setFavoriteStepIds(ids);
      } catch (err) {
        console.error("❌ Failed to fetch favorites:", err);
      }
    };
    fetchFavorites();
  }, [auth?.userId, axiosPrivate]);

  // ✅ Toggle favorite (add/remove)
  const toggleFavorite = async (stepId) => {
    if (!auth?.userId) return;

    const isFavorite = favoriteStepIds.includes(stepId);
    setUpdatingFavorites((prev) => ({ ...prev, [stepId]: true }));

    try {
      if (isFavorite) {
        // Optimistic UI
        setFavoriteStepIds((prev) => prev.filter((id) => id !== stepId));
        await axiosPrivate.delete(`/steps/${stepId}/favorite`, {
          data: { userId: auth.userId },
        });
      } else {
        setFavoriteStepIds((prev) => [...prev, stepId]);
        await axiosPrivate.post(`/steps/${stepId}/favorite`, {
          userId: auth.userId,
        });
      }
    } catch (err) {
      console.error("❌ Failed to toggle favorite:", err);
    } finally {
      setUpdatingFavorites((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  if (error) return <Error error={error} isNavOpen={isNavOpen} />;

  return (
    <div className={`${isNavOpen ? "body-squeezed" : "body"}`}>
      <h2 className="role-page-title">
        {t("rolePage.title", { role: role.role_name })}
      </h2>

      <FilterRoleSteps
        stepName={stepNameFilter}
        onStepNameChange={handleFilterChange}
      />

      {isLoading ? (
        <LoadingSpinner />
      ) : steps.length === 0 ? (
        <p>{t("rolePage.noSteps")}</p>
      ) : (
        <div className="steps-grid">
          {steps.map((step) => {
            const isFav = favoriteStepIds.includes(step.step_id);
            const isToggling = updatingFavorites[step.step_id];

            return (
              <div
                key={step.step_id}
                className="step-card"
                onClick={() => navigate(`/roles/${role.role_slug}/steps/${step.step_id}`)}
              >
                <button
                  className="star-button"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent navigation
                    toggleFavorite(step.step_id);
                  }}
                  disabled={isToggling}
                  title={
                    isFav
                      ? t("rolePage.removeFromFavorites", "Remove from favorites")
                      : t("rolePage.addToFavorites", "Add to favorites")
                  }
                >
                  {isFav ? "★" : "☆"}
                </button>

                <div className="step-name">{step.step_name}</div>
              </div>
            );
          })}


        </div>
      )}
    </div>
  );
}
